import { PermissionProvider, Permissions } from './Permissions';
import { Observable, Subject } from 'rxjs';
import isEqual from 'lodash.isequal';
import { WlWindow, Cordova } from 'typings/globals/cordova';
import Analytics from '@withlocals/withlocals-analytics';
import { isCordova } from 'utils/platform';

declare const window: WlWindow;
declare let cordova: Cordova;

export class CordovaPermissions implements PermissionProvider {
  private permissionsSubject = new Subject<Permissions>();

  getBackgroundRefresh(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (cordova.plugins.diagnostic.isBackgroundRefreshAuthorized) {
        cordova.plugins.diagnostic.isBackgroundRefreshAuthorized(resolve, reject);
      } else {
        resolve(true);
      }
    });
  }

  getNotifications(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!cordova.plugins.diagnostic.getRemoteNotificationsAuthorizationStatus) {
        return resolve(true);
      }
      cordova.plugins.diagnostic.getRemoteNotificationsAuthorizationStatus((status) => {
        console.log('getRemoteNotificationsAuthorizationStatus', status);
        switch (status) {
          case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
            console.log('Permission not yet requested');
            resolve(null);
            break;
          case cordova.plugins.diagnostic.permissionStatus.DENIED:
            console.log('Permission denied');
            resolve(false);
            break;
          case cordova.plugins.diagnostic.permissionStatus.GRANTED:
            console.log('Permission granted');
            resolve(true);
            break;
          default:
            resolve(false);
            break;
        }
      }, (error) => {
        console.error('The following error occurred: ' + error);
      });
    });
  }

  getLocationInUse(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.diagnostic.getLocationAuthorizationStatus(resolve, reject);
    })
      .then((status) => {
        return status === cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE
          || status === cordova.plugins.diagnostic.permissionStatus.GRANTED;
      });
  }

  /**
   * Returns a promise whether the GPS on the device is enabled or not
   * @returns {Promise<boolean>}
   */
  getGpsEnabled(): Promise<boolean> {
    return new Promise(resolve =>
      cordova.plugins.diagnostic.isLocationEnabled(resolve, () => resolve(false))
    );
  }

  canGetMoreLocationPermissions(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.diagnostic.getLocationAuthorizationStatus(resolve, reject);
    })
      .then((status) => {
        console.debug('getLocationAuthorizationStatus', status);
        switch (status) {
          case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
            return true;
          case cordova.plugins.diagnostic.permissionStatus.DENIED:
            // on android we can still ask for more
            return (isCordova() && window.device && window.device.platform === 'Android');
          case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
            // on android we can still ask for more
            return false;
          case cordova.plugins.diagnostic.permissionStatus.GRANTED:
            return false;
          case cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
            return true;
          default:
            return false;
        }
      });
  }

  getLocationBackground(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.diagnostic.getLocationAuthorizationStatus(resolve, reject);
    })
      .then(status => (status === cordova.plugins.diagnostic.permissionStatus.GRANTED));
  }

  getHighAccuracyPermission(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (window.cordova && window.cordova.platformId === 'android') {
        // Android
        cordova.plugins.diagnostic.getLocationMode(resolve, reject);
      } else {
        // iOS (resolve with any value, as this is ignored)
        resolve(-1);
      }
    })
      .then((status) => {
        if (window.cordova && window.cordova.platformId === 'android') {
          return status === cordova.plugins.diagnostic.locationMode.HIGH_ACCURACY;
        }
        return false;
      });
  }

  getWifi(): Promise<boolean> {
    return new Promise(cordova.plugins.diagnostic.isWifiAvailable)
      .catch((e) => {
        console.log('error getting wifi permissions', e);
        return false;
      }) as Promise<boolean>;
  }

  getCamera(): Promise<boolean> {
    return new Promise(cordova.plugins.diagnostic.isCameraAvailable)
      .catch((e) => {
        console.log('error getting camera permissions', e);
        return false;
      }) as Promise<boolean>;
  }

  permissions = (): Promise<Permissions> => {
    console.debug('permissions, getting them.');
    return Promise.all([
      this.getBackgroundRefresh(),
      this.getNotifications(),
      this.getLocationBackground(),
      this.getLocationInUse(),
      this.getGpsEnabled(),
      this.getHighAccuracyPermission(),
      this.canGetMoreLocationPermissions(),
      this.getWifi(),
      this.getCamera()])
      .then((
        [
          backgroundRefresh,
          push,
          locationBackground,
          locationInUse,
          gpsEnabled,
          getHighAccuracyPermission,
          canRequestBackground,
          wifi,
          camera,
        ]) => {
        return {
          backgroundRefresh,
          push, wifi, camera,
          location: {
            gpsEnabled,
            getHighAccuracyPermission,
            canRequestBackground,
            inUse: locationInUse,
            background: locationBackground,
            canRequestInUse: canRequestBackground && !gpsEnabled,
            canReRequest: true,
          },
        };
      }).then((p) => {
        console.log('permissions()', p);
        this.permissionsSubject.next(p);
        return p;
      });
  };

  requestLocationInUsePermission(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.diagnostic.requestLocationAuthorization(
        resolve,
        reject,
        cordova.plugins.diagnostic.locationAuthorizationMode.WHEN_IN_USE);
    }).then((status) => {
      setTimeout(this.permissions);
      switch (status) {
        case cordova.plugins.diagnostic.permissionStatus.GRANTED:
          Analytics.track('App', 'location permissions', 'always');
          return true;
        case cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
          Analytics.track('App', 'location permissions', 'in-use');
          return true;
        default:
          Analytics.track('App', 'location permissions', 'never');
          return false;
      }
    });
  }

  requestLocationBackgroundPermission(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.diagnostic.requestLocationAuthorization(
        resolve,
        reject,
        cordova.plugins.diagnostic.locationAuthorizationMode.ALWAYS);
    }).then((status) => {
      console.log(status);
      setTimeout(this.permissions);
      switch (status) {
        case cordova.plugins.diagnostic.permissionStatus.GRANTED:
          Analytics.track('App', 'location permissions', 'always');
          return true;
        case cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
          Analytics.track('App', 'location permissions', 'in-use');
          return true;
        default:
          Analytics.track('App', 'location permissions', 'never');
          return false;
      }
    });
  }

  requestHighAccuracyLocationPermission(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      cordova.plugins.locationAccuracy.request(resolve, reject, cordova.plugins.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY);
    })
      .then((status) => {
        if (window.cordova && window.cordova.platformId === 'android') {
          // android
          return (status === cordova.plugins.locationAccuracy.SUCCESS_USER_AGREED
            || status === cordova.plugins.locationAccuracy.SUCCESS_SETTINGS_SATISFIED);
        }
        // ios
        return true;
      }, (error) => {
        console.log('request High Accuracy Location Permission error', error);
        return false;
      });
  }

  public askForLocationPermissionsAfterDenial(): Promise<boolean> {
    return new Promise((resolve) => {
      window.navigator.notification.confirm(
        `To find awesome things to do near you:
        1. Click “Open Settings“
        2. Go to Withlocals
        3. Click location and set it to “Always”
        4. Return to the app and enjoy!`,
        // 'To find awesome things to do near you, click \'Open Settings\' and set location access to \'Always\'',  // message
        resolve,
        'Location access currently \'Disabled\'',
        ['Open Settings', 'Cancel']             // buttonLabels
      );
    })
      .then(buttonIndex => buttonIndex === 1)
      .then((goToSettings) => {
        if (goToSettings) {
          Analytics.track('App', 'Open settings', 'Location sharing reminder');
          window?.cordova?.plugins?.diagnostic?.switchToSettings?.();
          return Observable.fromEvent(document, 'getLocation')
            .debounceTime(2000)
            .map(() => true)
            .do(() => console.debug('resuming app after settings'))
            .toPromise();
        }
        Analytics.track('App', 'Cancel', 'Location sharing reminder');
        return goToSettings;
      });
  }

  readonly permissions$: Observable<Permissions> = this.permissionsSubject
    .distinctUntilChanged(isEqual)
    .shareReplay(1);

  constructor() {
    document.addEventListener('deviceready', () => {
      this.permissions();
      cordova.plugins.diagnostic.registerLocationStateChangeHandler(this.permissions);
    });
    document.addEventListener('resume', () => {
      this.permissions();
    });

    this.permissions$.subscribe(p => console.log('permissions$', JSON.stringify(p)));

  }
}
